/**
 * 🎯 Header Component
 * 
 * SignalR ilə real-time bildiriş sayı yeniləmələri.
 * 
 * @version 2.0.0
 */

import React, { useState, useContext, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Header.css";

import { HerracContext } from "../../../App";
import { useSignalR } from "../../services/SignalRContext";
import { 
  FaListUl, 
  FaBars, 
  FaTimes, 
  FaGavel, 
  FaPlus,
  FaUser,
  FaSignOutAlt,
  FaChartLine,
  FaBell,
  FaWifi
} from "react-icons/fa";
import axios from "axios";

function Header() {
  const navigate = useNavigate();
  const { 
    setSearchQuery, 
    user, 
    setUser, 
    setSelectedCategory, 
    setSelectedSubcategory,
    unreadNotificationCount,
    setUnreadNotificationCount
  } = useContext(HerracContext);

  // SignalR
  const { isAuctionConnected, onNotification } = useSignalR();

  // State-lər
  const [inputValue, setInputValue] = useState("");
  const [mobileMenuActive, setMobileMenuActive] = useState(false);
  const [categoriesDropdownOpen, setCategoriesDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);

  // Refs
  const mobileMenuRef = useRef(null);
  const categoriesDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);

  // ============================================
  // SIGNALR - REAL-TIME NOTIFICATIONS
  // ============================================
  useEffect(() => {
    if (!user) return;

    // SignalR-dən real-time bildiriş gəldikdə
    const unsubscribe = onNotification((notification) => {
      console.log('🔔 Header: Yeni bildiriş alındı', notification);
      setUnreadNotificationCount(prev => prev + 1);
    });

    return () => unsubscribe();
  }, [user, onNotification, setUnreadNotificationCount]);

  // Ekran ölçüsü dəyişiklik - Mobil menü qapalı qalsın
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);
      
      if (mobileMenuActive && newWidth > 768) {
        setMobileMenuActive(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mobileMenuActive]);

  // Kenara klik - Mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuActive(false);
      }
    };

    if (mobileMenuActive) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [mobileMenuActive]);

  // Kenara klik - Categories dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoriesDropdownRef.current && !categoriesDropdownRef.current.contains(event.target)) {
        setCategoriesDropdownOpen(false);
      }
    };

    if (categoriesDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [categoriesDropdownOpen]);

  // Kenara klik - User dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };

    if (userDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userDropdownOpen]);

  // Axtarış
  const handleSearch = (event) => {
    event.preventDefault();
    if (inputValue.trim()) {
      setSearchQuery(inputValue.trim());
      setMobileMenuActive(false);
      navigate('/');
    }
  };

  // Çıxış
  const handleLogout = async () => {
    try {
      await axios.post('https://api.herracim.az/api/account/logout', {}, {
        withCredentials: true
      });
      setUser(null);
      setUserDropdownOpen(false);
      setMobileMenuActive(false);
      setUnreadNotificationCount(0);
      navigate("/");
      window.location.reload();
    } catch (error) {
      console.error("Çıxış zamanı xəta:", error);
    }
  };

  // Logo click
  const handleLogoClick = () => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSearchQuery("");
    setMobileMenuActive(false);
    navigate("/");
  };

  // Mobile menu toggle
  const toggleMobileMenu = () => {
    setMobileMenuActive(!mobileMenuActive);
  };

  // Categories dropdown toggle
  const toggleCategoriesDropdown = () => {
    setCategoriesDropdownOpen(!categoriesDropdownOpen);
  };

  // User dropdown toggle
  const toggleUserDropdown = () => {
    setUserDropdownOpen(!userDropdownOpen);
  };

  // Navigate to notifications
  const handleNotificationsClick = () => {
    navigate('/kabinetim/bildirislerim');
    setUserDropdownOpen(false);
    setMobileMenuActive(false);
  };

  // Mobile link click
  const handleMobileLinkClick = () => {
    setMobileMenuActive(false);
  };

  return (
    <>
      <header className="auction-header">
        <div className="auction-header__container">
          {/* Left - Categories & Logo */}
          <div className="auction-header__left">
            <div className="auction-header__categories-dropdown" ref={categoriesDropdownRef}>
              <button 
                className="auction-header__categories-btn"
                onClick={toggleCategoriesDropdown}
                title="Kateqoriyalar"
              >
                <FaListUl />
                <span>Kateqoriyalar</span>
              </button>
              
              {categoriesDropdownOpen && (
                <ul className="auction-header__categories-menu">
                  <li><Link to="/mexfilik-siyaseti" onClick={() => setCategoriesDropdownOpen(false)}>🛡 Məxfilik Siyasəti</Link></li>
                  <li><Link to="/istifade-sertleri" onClick={() => setCategoriesDropdownOpen(false)}>📃 İstifadə Şərtləri</Link></li>
                  <li><Link to="/tez-tez-verilen-suallar" onClick={() => setCategoriesDropdownOpen(false)}>❓ Tez-tez verilən suallar</Link></li>
                  <li><Link to="/elaqe" onClick={() => setCategoriesDropdownOpen(false)}>📞 Əlaqə</Link></li>
                </ul>
              )}
            </div>

            <Link to="/" className="auction-header__logo" onClick={handleLogoClick}>
              <img src={`/images/logo.png`} alt="Herracim.az Logo" />
              <span className="auction-header__logo-text">HERRACIM.AZ</span>
            </Link>
          </div>

          {/* Right - Actions */}
          <div className="auction-header__actions">
            {/* Connection Status Indicator */}
            {user && (
              <div 
                className={`auction-header__connection-status ${isAuctionConnected ? 'connected' : 'disconnected'}`}
                title={isAuctionConnected ? 'Real-time bağlantı aktiv' : 'Bağlantı yoxdur'}
              >
                <FaWifi />
              </div>
            )}

            <Link to="/yeni-herrac" className="auction-header__new-auction-btn">
              <FaGavel />
              <span>Yeni Hərrac</span>
            </Link>

            {user ? (
              <div className="auction-header__user" ref={userDropdownRef}>
                <div className="auction-header__user-info" onClick={toggleUserDropdown}>
                  <img
                    src={
                      user?.profileImageUrl && user.profileImageUrl !== "NULL"
                        ? `https://api.herracim.az${user.profileImageUrl}`
                        : `/images/profile.svg`
                    }
                    alt="Profile"
                    className="auction-header__user-avatar"
                  />
                  <div className="auction-header__user-details">
                    <span className="auction-header__user-name">{user.fullName}</span>
                    <span className="auction-header__user-balance">
                      💰 {user.balance?.toFixed(2) || '0.00'} AZN
                    </span>
                  </div>
                  
                  {/* Notification badge on avatar */}
                  {unreadNotificationCount > 0 && (
                    <span className="auction-header__notification-badge">
                      {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                    </span>
                  )}
                </div>

                {userDropdownOpen && (
                  <ul className="auction-header__user-menu">
                    <li>
                      <Link to="/kabinetim" onClick={() => setUserDropdownOpen(false)}>
                        <FaUser /> Kabinetim
                      </Link>
                    </li>
                    <li>
                      <Link to="/kabinetim/herraclarim" onClick={() => setUserDropdownOpen(false)}>
                        <FaGavel /> Hərraclarım
                      </Link>
                    </li>
                    <li>
                      <Link to="/kabinetim/tekliflerim" onClick={() => setUserDropdownOpen(false)}>
                        <FaChartLine /> Təkliflərim
                      </Link>
                    </li>
                    <li>
                      <button 
                        onClick={handleNotificationsClick}
                        className="auction-header__menu-button"
                      >
                        <FaBell /> Bildirişlər
                        {unreadNotificationCount > 0 && (
                          <span className="auction-header__menu-badge">
                            {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                          </span>
                        )}
                      </button>
                    </li>
                    <li className="auction-header__divider"></li>
                    <li>
                      <button onClick={handleLogout} className="auction-header__logout-btn">
                        <FaSignOutAlt /> Çıxış
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            ) : (
              <Link to="/giris" className="auction-header__login-btn">
                <FaUser />
                <span>Giriş</span>
              </Link>
            )}
          </div>

          {/* Mobile Menu Icon */}
          <button className="auction-header__mobile-toggle" onClick={toggleMobileMenu}>
            {mobileMenuActive ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuActive && (
        <div className="auction-header__mobile-overlay" onClick={toggleMobileMenu}></div>
      )}

      {/* Mobile Menu */}
      <div className={`auction-header__mobile-menu ${mobileMenuActive ? 'active' : ''}`} ref={mobileMenuRef}>
        <div className="auction-header__mobile-header">
          <h3>
            <FaGavel /> Herracim.az
            {isAuctionConnected && (
              <FaWifi style={{ marginLeft: '8px', color: '#22c55e', fontSize: '0.8em' }} />
            )}
          </h3>
          <button className="auction-header__mobile-close" onClick={toggleMobileMenu}>
            <FaTimes />
          </button>
        </div>

        {/* Mobile User Info */}
        {user && (
          <div className="auction-header__mobile-user">
            <img
              src={
                user?.profileImageUrl && user.profileImageUrl !== "NULL"
                  ? `https://api.herracim.az${user.profileImageUrl}`
                  : `/images/profile.svg`
              }
              alt="Profile"
            />
            <div className="auction-header__mobile-user-details">
              <span className="auction-header__mobile-user-name">{user.fullName}</span>
              <span className="auction-header__mobile-user-balance">
                💰 Balans: {user.balance?.toFixed(2) || '0.00'} AZN
              </span>
            </div>
          </div>
        )}

        {/* Mobile Actions */}
        <div className="auction-header__mobile-actions">
          <Link to="/yeni-herrac" className="auction-header__mobile-btn auction-header__mobile-btn--primary" onClick={handleMobileLinkClick}>
            <FaPlus /> Yeni Hərrac Yarat
          </Link>

          {user ? (
            <>
              <Link to="/kabinetim" className="auction-header__mobile-btn" onClick={handleMobileLinkClick}>
                <FaUser /> Kabinetim
              </Link>
              <Link to="/kabinetim/herraclarim" className="auction-header__mobile-btn" onClick={handleMobileLinkClick}>
                <FaGavel /> Mənim Hərraclarım
              </Link>
              <Link to="/kabinetim/tekliflerim" className="auction-header__mobile-btn" onClick={handleMobileLinkClick}>
                <FaChartLine /> Təkliflərim
              </Link>
              <button
                onClick={handleNotificationsClick}
                className="auction-header__mobile-btn"
              >
                <FaBell /> Bildirişlər
                {unreadNotificationCount > 0 && (
                  <span className="auction-header__mobile-badge">
                    {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                  </span>
                )}
              </button>
              <button onClick={handleLogout} className="auction-header__mobile-btn auction-header__mobile-btn--logout">
                <FaSignOutAlt /> Çıxış
              </button>
            </>
          ) : (
            <Link to="/giris" className="auction-header__mobile-btn auction-header__mobile-btn--primary" onClick={handleMobileLinkClick}>
              <FaUser /> Giriş
            </Link>
          )}
        </div>

        {/* Mobile Info Pages */}
        <div className="auction-header__mobile-info">
          <h4>Məlumat</h4>
          <ul>
            <li><Link to="/mexfilik-siyaseti" onClick={handleMobileLinkClick}>🛡 Məxfilik Siyasəti</Link></li>
            <li><Link to="/istifade-sertleri" onClick={handleMobileLinkClick}>📃 İstifadə Şərtləri</Link></li>
            <li><Link to="/tez-tez-verilen-suallar" onClick={handleMobileLinkClick}>❓ Tez-tez verilən suallar</Link></li>
            <li><Link to="/elaqe" onClick={handleMobileLinkClick}>📞 Əlaqə</Link></li>
          </ul>
        </div>
      </div>
    </>
  );
}

export default Header;